import React from 'react';
import { CVData } from '../../types';
import { BaseTemplate } from './BaseTemplate';
import { generateTemplateColors } from '../../lib/colorUtils';

interface Template5Props {
  data: CVData;
  pageNumber?: number;
  customColor?: string;
}

export function Template5({ data, pageNumber = 1, customColor }: Template5Props) {
  // Usar el color personalizado si está disponible, o el color por defecto
  const baseColor = customColor || '#8FBC8F';
  
  console.log("Template5 - Color personalizado recibido:", customColor);
  console.log("Template5 - Color base utilizado:", baseColor);
  
  // Generar colores derivados basados en el color principal
  const colors = generateTemplateColors(baseColor);
  
  console.log("Template5 - Colores generados:", colors);

  // Preparar habilidades como palabras únicas eliminando prefijos comunes
  const optimizedSkills = data.skills?.map(skill => {
    if (!skill) return '';
    
    // Convertir a minúsculas para comparación, pero mantener el formato original
    const skillLower = skill.toLowerCase();
    const originalSkill = skill;
    
    // Lista de prefijos comunes a eliminar
    const prefijos = [
      'programación en', 'programacion en', 'experiencia en', 'conocimiento de', 
      'conocimientos de', 'desarrollo de', 'desarrollo en', 'implementación de', 
      'implementacion de', 'uso de', 'manejo de', 'gestión de', 'gestion de',
      'habilidades en', 'capacidad para', 'competencia en'
    ];
    
    let result = originalSkill;
    
    // Eliminar prefijos
    for (const prefijo of prefijos) {
      if (skillLower.startsWith(prefijo)) {
        // Extraer solo lo que viene después del prefijo
        result = originalSkill.substring(prefijo.length).trim();
        break;
      }
    }
    
    // Si aún hay demasiadas palabras, tomar solo la primera o dos primeras
    const words = result.split(' ');
    if (words.length > 2) {
      // Para conceptos técnicos específicos, intentar mantener términos comunes
      const tecnologias = ['Power BI', 'Machine Learning', 'Microsoft Excel', 'Data Science'];
      
      // Verificar si alguna de las tecnologías está presente en la habilidad
      for (const tech of tecnologias) {
        if (result.includes(tech)) {
          return tech;
        }
      }
      
      // Si no hay coincidencia con tecnologías conocidas, tomar las primeras palabras
      return words.slice(0, 2).join(' ');
    }
    
    return result;
  }).slice(0, 6) || []; // Limitar a 6 habilidades máximo

  // Crear una versión modificada de los datos con las habilidades optimizadas
  const optimizedData = {
    ...data,
    skills: optimizedSkills
  };

  // La clase de estilo para la barra lateral y bordes
  const customSidebarStyle = 'bg-white/90 shadow-sm';
  const borderStyle = `border border-[${baseColor}]/30 rounded-3xl`;
  
  // Variables de control para la distribución de contenido (igual que Template1)
  // Verificamos si hay cursos para mostrar
  const hasCourses = data.courses && data.courses.length > 0;
  
  // Verificamos si es una página de continuación
  const isContinuationPage = (data as any).isContinuationPage || false;
  
  // Verificamos si es una página de continuación específica del caso 3
  const isCase3ContinuationPage = (data as any).isCase3ContinuationPage || false;
  
  // Verificamos si es una página de continuación específica del caso 4
  const isCase4ContinuationPage = (data as any).isCase4ContinuationPage || false;
  
  // Verificamos si es la segunda página del caso 5 (3 experiencias)
  const isCase5SecondPage = (data as any).isCase5SecondPage || false;
  
  // Verificamos si es la tercera página del caso 5 (solo cursos)
  const isCase5ThirdPage = (data as any).isCase5ThirdPage || false;
  
  // Verificamos si es la segunda o tercera página del caso 6
  const isCase6SecondPage = (data as any).isCase6SecondPage || false;
  const isCase6ThirdPage = (data as any).isCase6ThirdPage || false;
  
  // Verificamos si es la segunda página del caso 7
  const isCase7SecondPage = (data as any).isCase7SecondPage || false;
  
  // Verificamos si es la tercera página del caso 7
  const isCase7ThirdPage = (data as any).isCase7ThirdPage || false;
  
  // Verificamos si es la segunda, tercera o cuarta página del caso 8
  const isCase8SecondPage = (data as any).isCase8SecondPage || false;
  const isCase8ThirdPage = (data as any).isCase8ThirdPage || false;
  const isCase8FourthPage = (data as any).isCase8FourthPage || false;
  
  // Verificamos si es una página de continuación con experiencia + cursos
  const isExperiencePlusCourses = (data as any).isExperiencePlusCourses || false;
  
  // Verificamos si es una página de continuación que incluye experiencias
  const isContinuationWithExperiences = (data as any).isContinuationWithExperiences || false;
  
  // Verificamos si es una página de sólo cursos
  const isOnlyCoursesPage = isCase5ThirdPage || isCase8FourthPage || (data as any).isOnlyCoursesPage || false;

  // Verificamos si es una página de continuación para cualquier caso
  const isAnyCaseContinuationPage = isCase3ContinuationPage || isCase4ContinuationPage || 
                                   isCase5SecondPage || isCase5ThirdPage || 
                                   isCase6SecondPage || isCase6ThirdPage || 
                                   isCase7SecondPage || isCase7ThirdPage ||
                                   isCase8SecondPage || isCase8ThirdPage || isCase8FourthPage;

  console.log("Template5 - Es continuación:", isContinuationPage);
  console.log("Template5 - Hay cursos:", hasCourses);
  console.log("Template5 - Longitud de experiencias:", data.experience?.length || 0);
  console.log("Template5 - Longitud de cursos:", data.courses?.length || 0);

  // Renderizado de los cursos
  const renderCourses = () => {
    if (!data.courses || data.courses.length === 0) {
      console.log("Template5 - No hay cursos para renderizar");
      return null;
    }
    
    console.log("Template5 - Renderizando cursos:", data.courses.length);
    return (
      <div className={`p-5 pb-4 bg-white ${borderStyle} shadow-sm mt-5`}>
        <h3 className="text-xl font-light mb-4" style={{ color: colors.accentColor }}>Cursos y Actividades Adicionales</h3>
        <div className="relative">
          {/* Línea vertical que conecta todos los elementos */}
          <div 
            className="absolute left-2 top-0 bottom-0 w-0.5" 
            style={{ backgroundColor: `${colors.accentColor}40` }}
          ></div>
          
          {/* Contenedor para todos los cursos */}
          <div className="space-y-3 ml-10">
            {data.courses.map((course, index) => (
              <div key={`course-${index}`} className="relative">
                {/* Punto en la línea de tiempo */}
                <div 
                  className="absolute -left-10 top-1.5 w-4 h-4 rounded-full border-2"
                  style={{ 
                    backgroundColor: 'white', 
                    borderColor: colors.accentColor 
                  }}
                ></div>
                
                {/* Contenido del curso */}
                <div>
                  <h4 className="font-medium text-gray-800">{course.title}</h4>
                  <div className="flex items-center">
                    <p className="text-sm" style={{ color: colors.accentColor }}>{course.institution}</p>
                    <span className="text-sm mx-2" style={{ color: colors.accentColor }}>|</span>
                    <p className="text-sm" style={{ color: colors.accentColor }}>{course.period}</p>
                  </div>
                  {course.description && (
                    <p className="text-sm text-gray-600 mt-1">{course.description}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Si es una página solo de cursos, solo mostramos los cursos
  if (isOnlyCoursesPage && hasCourses) {
    console.log("Template5 - Renderizando SOLO cursos sin otro contenido");
    return (
      <BaseTemplate
        data={optimizedData}
        colors={colors}
        isTemplate1={false}
        sidebarStyle={customSidebarStyle}
        renderMainContent={() => (
          <div className="py-5">
            {renderCourses()}
          </div>
        )}
      />
    );
  }
  
  // Si es una página de continuación con experiencias
  if (isAnyCaseContinuationPage && data.experience && data.experience.length > 0) {
    console.log("Template5 - Renderizando continuación con experiencia");
    
    // Para los casos específicos, determinar si se deben mostrar cursos
    const shouldShowCoursesInContinuation = 
      hasCourses && 
      (isCase3ContinuationPage || isCase4ContinuationPage || isCase6ThirdPage || isCase7ThirdPage) && 
      !(isCase5SecondPage || isCase6SecondPage || isCase7SecondPage || 
        isCase8SecondPage || isCase8ThirdPage);
    
    return (
      <BaseTemplate
        data={optimizedData}
        colors={colors}
        isTemplate1={false}
        sidebarStyle={customSidebarStyle}
        renderMainContent={(experienceItems) => (
          <div className="space-y-5">
            {/* Experiencia laboral (continuación) */}
            {experienceItems && React.Children.count(experienceItems) > 0 && (
              <div className={`p-5 pb-4 bg-white ${borderStyle} shadow-sm mb-5`}>
                <h3 className="text-xl font-light mb-4" style={{ color: colors.accentColor }}>
                  Experiencia laboral (continuación)
                </h3>
                <div className="relative">
                  {/* Línea vertical que conecta todos los elementos */}
                  <div 
                    className="absolute left-2 top-0 bottom-0 w-0.5" 
                    style={{ backgroundColor: `${colors.accentColor}40` }}
                  ></div>
                  
                  {/* Contenedor para todas las experiencias */}
                  <div className="space-y-4 ml-10">
                    {React.Children.map(experienceItems, (item, index) => (
                      <div key={`exp-case-${index}`} className="relative">
                        {/* Punto en la línea de tiempo */}
                        <div 
                          className="absolute -left-10 top-1.5 w-4 h-4 rounded-full border-2"
                          style={{ 
                            backgroundColor: 'white', 
                            borderColor: colors.accentColor 
                          }}
                        ></div>
                        {item}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
            
            {/* Cursos y Actividades (para casos específicos) */}
            {shouldShowCoursesInContinuation && renderCourses()}
          </div>
        )}
      />
    );
  }

  return (
    <BaseTemplate
      data={optimizedData}
      colors={colors}
      isTemplate1={false}
      sidebarStyle={customSidebarStyle}
      renderMainContent={(experienceItems, pageNumber) => (
        <>
          {pageNumber === 1 ? (
            <>
              {/* Encabezado - nombre en negrita en vez de mayúsculas */}
              <div className={`relative p-5 pb-4 bg-white ${borderStyle} shadow-sm mb-5`}>
                <h1 className="text-3xl font-bold text-gray-800 mb-0.5">{data.name}</h1>
                <h2 className="text-lg font-bold mb-1" style={{ color: colors.accentColor }}>{data.title}</h2>
              </div>

              {/* Education Section */}
              <div className="p-4 mb-4 border-b border-gray-200">
                <h3 className="text-base font-medium mb-3" style={{ color: colors.accentColor }}>Educación</h3>
                <div className="relative">
                  {/* Línea vertical que conecta todos los elementos */}
                  <div 
                    className="absolute left-2 top-0 bottom-0 w-0.5" 
                    style={{ backgroundColor: `${colors.accentColor}40` }}
                  ></div>
                  
                  {/* Contenedor para todos los elementos de educación */}
                  <div className="space-y-3 ml-10">
                    {data.education.map((edu, index) => (
                      <div key={`edu-${index}`} className="relative">
                        {/* Punto en la línea de tiempo */}
                        <div 
                          className="absolute -left-10 top-1.5 w-4 h-4 rounded-full border-2"
                          style={{ 
                            backgroundColor: 'white', 
                            borderColor: colors.accentColor 
                          }}
                        ></div>
                        
                        {/* Contenido de educación */}
                        <div>
                          <h4 className="font-bold text-gray-800 text-sm">{edu.degree}</h4>
                          <p className="text-xs text-gray-600">{edu.institution}</p>
                          <p className="text-xs text-gray-500">{edu.period}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Experience Section */}
              <div className="p-4 mb-4 border-b border-gray-200">
                <h3 className="text-base font-medium mb-3" style={{ color: colors.accentColor }}>Experiencia Profesional</h3>
                <div className="relative">
                  {/* Línea vertical que conecta todos los elementos */}
                  <div 
                    className="absolute left-2 top-0 bottom-0 w-0.5" 
                    style={{ backgroundColor: `${colors.accentColor}40` }}
                  ></div>
                  
                  {/* Contenedor para todas las experiencias */}
                  <div className="space-y-4 ml-10">
                    {React.Children.map(experienceItems, (item, index) => (
                      <div key={`exp-${index}`} className="relative">
                        {/* Punto en la línea de tiempo */}
                        <div 
                          className="absolute -left-10 top-1.5 w-4 h-4 rounded-full border-2"
                          style={{ 
                            backgroundColor: 'white', 
                            borderColor: colors.accentColor 
                          }}
                        ></div>
                        {item}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Si es otra página, mostramos contenido alternativo según el caso */}
              {/* Experiencia laboral (continuación) */}
              {experienceItems && React.Children.count(experienceItems) > 0 && (
                <div className="p-4 mb-4 border-b border-gray-200">
                  <h3 className="text-base font-medium mb-3" style={{ color: colors.accentColor }}>Experiencia Profesional (continuación)</h3>
                  <div className="relative">
                    {/* Línea vertical que conecta todos los elementos */}
                    <div 
                      className="absolute left-2 top-0 bottom-0 w-0.5" 
                      style={{ backgroundColor: `${colors.accentColor}40` }}
                    ></div>
                    
                    {/* Contenedor para todas las experiencias */}
                    <div className="space-y-4 ml-10">
                      {React.Children.map(experienceItems, (item, index) => (
                        <div key={`exp-cont-${index}`} className="relative">
                          {/* Punto en la línea de tiempo */}
                          <div 
                            className="absolute -left-10 top-1.5 w-4 h-4 rounded-full border-2"
                            style={{ 
                              backgroundColor: 'white', 
                              borderColor: colors.accentColor 
                            }}
                          ></div>
                          {item}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Cursos y Actividades Adicionales */}
              {hasCourses && !isCase5SecondPage && !isCase6SecondPage && !isCase7SecondPage && 
               !isCase8SecondPage && !isCase8ThirdPage && renderCourses()}
            </>
          )}
        </>
      )}
    />
  );
}